import React, { useState, useEffect, useRef } from 'react';

interface CurrencyDropdownProps {
  onCurrencyChange: (currency: string, multiplier: number) => void;
}

export default function CurrencyDropdown({ onCurrencyChange }: CurrencyDropdownProps) {
  const [currency, setCurrency] = useState(() => {
    try {
      const savedCurrency = localStorage.getItem('currency');
      return savedCurrency || 'USD';
    } catch (error) {
      console.error('Error reading currency from localStorage:', error);
      return 'USD';
    }
  });
  const [currencyMultiplier, setCurrencyMultiplier] = useState(1);
  const [isCurrencyDropdownOpen, setIsCurrencyDropdownOpen] = useState(false);
  const currencyDropdownRef = useRef<any>(null);

  useEffect(() => {
    try {
      const savedCurrency = localStorage.getItem('currency');
      if (savedCurrency) return;

      const geoCountry = localStorage.getItem('footerGeoCountry');
      const geoTs = Number(localStorage.getItem('footerGeoTimestamp') || 0);
      const geoFresh = geoCountry && geoTs && Date.now() - geoTs < 6 * 30 * 24 * 60 * 60 * 1000;
      const isIndianByGeo = geoFresh ? geoCountry.toUpperCase() === 'IN' : null;
      const isIndianByHeuristic =
        typeof window !== 'undefined' &&
        (window as { __isIndianLocale__?: boolean }).__isIndianLocale__ === true;

      const defaultCurrency = (isIndianByGeo ?? isIndianByHeuristic) ? 'INR' : 'USD';
      localStorage.setItem('currency', defaultCurrency);
      setCurrency(defaultCurrency);
    } catch {
      try {
        localStorage.setItem('currency', 'INR');
      } catch {
        void 0;
      }
      setCurrency('INR');
    }
  }, []);

  useEffect(() => {
    setCurrencyMultiplier(1);
    try {
      localStorage.setItem('currencyMultiplier', '1');
    } catch {
      void 0;
    }
    onCurrencyChange(currency, 1);
  }, [currency, onCurrencyChange]);

  /*
  // Fetch currency conversion rate (disabled: using separate USD/INR plan pricing)
  useEffect(() => {
    const fetchConversionRate = async () => {
      try {
        if (currency !== 'INR') {
          setCurrencyMultiplier(1);
          localStorage.setItem('currencyMultiplier', '1');
          onCurrencyChange(currency, 1);
          return;
        }

        const response = await fetch('https://api.exchangerate-api.com/v4/latest/USD');
        const data = await response.json();
        const inrRate = data.rates.INR;
        setCurrencyMultiplier(inrRate);
        localStorage.setItem('currencyMultiplier', inrRate.toString());
        onCurrencyChange(currency, inrRate);
      } catch (error) {
        console.error('Error fetching conversion rate:', error);
        const fallbackRate = currency === 'INR' ? 83.5 : 1;
        setCurrencyMultiplier(fallbackRate);
        localStorage.setItem('currencyMultiplier', String(fallbackRate));
        onCurrencyChange(currency, fallbackRate);
      }
    };
    fetchConversionRate();
  }, [currency, onCurrencyChange]);
  */

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (currencyDropdownRef.current && !currencyDropdownRef.current?.contains(event.target)) {
        setIsCurrencyDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCurrencyDropdownOpen]);

  // Toggle currency and save to localStorage
  const handleCurrencyChange = (newCurrency: string) => {
    setCurrency(newCurrency);
    setIsCurrencyDropdownOpen(false);
    try {
      localStorage.setItem('currency', newCurrency);
    } catch (error) {
      console.error('Error saving currency to localStorage:', error);
    }
  };

  return (
    <div className="relative inline-block text-left" ref={currencyDropdownRef}>
        <button
          onClick={() => setIsCurrencyDropdownOpen(!isCurrencyDropdownOpen)}
          className="currency-dropdown-button inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          {currency}
          <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {isCurrencyDropdownOpen && (
          <div className="currency-dropdown-menu absolute right-0 top-10 mt-2 w-32 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
            <div className="py-1">
              <button
                 onClick={() => handleCurrencyChange('USD')}
                 className={`currency-dropdown-option block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                   currency === 'USD' ? 'currency-dropdown-option-selected bg-blue-50 text-blue-700' : 'text-gray-700'
                 }`}
               >
                 USD
               </button>
               <button
                 onClick={() => handleCurrencyChange('INR')}
                 className={`currency-dropdown-option block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                   currency === 'INR' ? 'currency-dropdown-option-selected bg-blue-50 text-blue-700' : 'text-gray-700'
                 }`}
               >
                 INR
               </button>
            </div>
          </div>
        )}
    </div>
  );
}
